import { type RouteRecordRaw } from 'vue-router'

import { FeatureFlags } from '@/plugins/featureFlags'

/**
 * Feature flag to enable the new unified Pay view (labor summary + timesheets).
 * When enabled, the labor report is moved to the Pay view in the staff section.
 * When disabled, the labor report remains in reports-v2.
 *
 * Set in Unleash as: hermes.pay
 * Local override: VITE_FF_HERMES_PAY=true
 */
const PAY_VIEW_FLAG_NAME = 'hermes.pay'

export const brandReportingV2Routes: RouteRecordRaw[] = [
  {
    async beforeEnter() {
      // Enable MSW mocking for reports routes in development mode
      if (
        import.meta.env.MODE === 'development' &&
        import.meta.env.VITE_ENABLE_MOCKS === 'true'
      ) {
        const { startMockWorker, isMockWorkerStarted } =
          await import('@/mocks/browser')
        if (!isMockWorkerStarted()) {
          await startMockWorker()
        }
      }
    },
    children: [
      {
        component: async () => import('../views/SalesSummaryReport.vue'),
        meta: {
          title: 'reports-v2.salesSummaryReport',
        },
        name: `brand.reports-v2.sales`,
        path: 'sales-summary',
      },
      {
        component: async () => import('../views/PaymentMethodsReport.vue'),
        meta: {
          title: 'reports-v2.paymentMethodsReport',
        },
        name: `brand.reports-v2.paymentMethods`,
        path: 'payment-methods',
      },
      {
        component: async () => import('../views/PayoutStatsReport.vue'),
        meta: {
          authZ: ({ user }) =>
            user.can('read', 'reports') && user.can('read', 'payout'),
          title: 'reports-v2.payoutsStatsReport',
        },
        name: `brand.reports-v2.payoutsStats`,
        path: 'payout-stats',
      },
      {
        component: async () => import('../views/PayoutDetailPage.vue'),
        meta: {
          authZ: ({ user }) =>
            user.can('read', 'reports') && user.can('read', 'payout'),
          title: 'reports-v2.payoutsDetail',
        },
        name: `brand.reports-v2.payouts.detail`,
        path: 'payouts/:payoutId',
        props: true,
      },
      {
        component: async () => import('../views/TipsReport.vue'),
        meta: {
          title: 'reports-v2.tipsReport',
        },
        name: `brand.reports-v2.tips`,
        path: 'tips',
      },
      {
        component: async () => import('../views/FeesReport.vue'),
        meta: {
          title: 'reports-v2.feesReport',
        },
        name: `brand.reports-v2.fees`,
        path: 'fees',
      },
      {
        // Guard at the route so a flag-off deep link redirects before the
        // report can mount and fire its first refunds request. Evaluated per
        // navigation only — someone already on the page when the flag flips
        // keeps it until they navigate; the endpoint's authZ is the boundary.
        beforeEnter: () =>
          FeatureFlags.isEnabled('hermes.refundsView')
            ? true
            : { name: 'brand.reports.main' },
        component: async () => import('../views/RefundsReport.vue'),
        meta: {
          title: 'reports-v2.refundsReport',
        },
        name: `brand.reports-v2.refunds`,
        path: 'refunds',
      },
      {
        component: async () => import('../views/CashDrawerHistoryReport.vue'),
        meta: {
          title: 'reports-v2.cashDrawerHistoryReport',
        },
        name: `brand.reports-v2.cashDrawerHistory`,
        path: 'cash-drawer-history',
      },
      {
        component: async () => import('../views/CashDrawerSessionDetail.vue'),
        meta: {
          title: 'reports-v2.cashDrawerSessionDetail',
        },
        name: `brand.reports-v2.cashDrawerSession`,
        path: 'cash-drawer-history/:sessionId',
        props: true,
      },
      {
        component: async () => import('../views/CategorySalesReport.vue'),
        meta: {
          title: 'reports-v2.categorySalesReport',
        },
        name: `brand.reports-v2.categorySales`,
        path: 'sales-by-category',
      },
      {
        beforeEnter: () =>
          FeatureFlags.isEnabled('hermes.taxReportV2')
            ? true
            : { name: 'brand.reports.main' },
        component: async () => import('../views/TaxReport.vue'),
        meta: {
          title: 'reports-v2.taxReport',
        },
        name: `brand.reports-v2.tax`,
        path: 'tax',
      },
      {
        component: async () => import('../views/CouponBreakdownReport.vue'),
        meta: {
          title: 'reports-v2.couponBreakdownReport',
        },
        name: `brand.reports-v2.couponBreakdown`,
        path: 'coupon-breakdown',
      },
      {
        component: async () => import('../views/CouponBreakdownDetailPage.vue'),
        meta: {
          title: 'reports-v2.couponBreakdownDetail',
        },
        name: `brand.reports-v2.couponBreakdown.detail`,
        path: 'coupon-breakdown/:code',
        props: true,
      },
      {
        // Guard at the route so a flag-off deep link redirects before the
        // report can mount and fire its "Viewed" analytics event. Evaluated
        // per navigation only — someone already on the page when the flag
        // flips keeps it until they navigate; the component's own watch
        // handles that case, and the endpoint's authZ is the boundary.
        beforeEnter: () =>
          FeatureFlags.isEnabled('hermes.voidsReport')
            ? true
            : { name: 'brand.reports.main' },
        component: async () => import('../views/VoidsReport.vue'),
        meta: {
          title: 'reports-v2.voidsReport',
        },
        name: `brand.reports-v2.voids`,
        path: 'voids',
      },
      {
        component: async () => import('../views/CostOfLaborReport.vue'),
        meta: {
          title: 'reports-v2.costOfLaborReport',
        },
        name: `brand.reports-v2.cost-of-labor`,
        path: 'cost-of-labor',
      },
      // Only show labor report in reports-v2 if the new Pay view is disabled
      ...(!FeatureFlags.isEnabled(PAY_VIEW_FLAG_NAME)
        ? [
            {
              component: async () => import('../views/LaborReport.vue'),
              meta: {
                title: 'reports-v2.laborReport',
              },
              name: `brand.reports-v2.labor`,
              path: 'labor',
            },
          ]
        : []),
    ],
    meta: {
      authZ: ({ user }) => user.can('read', 'reports'),
    },
    path: 'reports-v2',
    redirect: { name: `brand.reports-v2.sales` }, // change to base list when ready
  },
]

// Remove location routes - everything goes through brand routes now with locationId as query param
export const locationReportingV2Routes: RouteRecordRaw[] = []
